import React from 'react';
import {formatDateTime} from '../';
import VoucherService from '../../services/Voucher/voucher.service';
import {
  renderTicketValidity,
  renderTicketStatus,
  renderTicketUsageType,
  renderTicketVisibility,
  ticketUsageTypeRadioOptions,
  TicketUsageTypeEnums,
} from '../voucher/VoucherUtils';
import {
  CreditVoucherParams,
  CreditVoucherValidationTypes,
  CsvFileInfosType,
} from '../../types/typeTargetedDistributions';
import {IsNullOrEmptyOrUndefined} from '../helpers';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Grid,
} from '@mui/material';
import {images} from '../../constants/image';
import {Typography} from '../../pages/targeted-distributions/TargetedDistributions.style';
import MembersService from '../../services/Members/members.service';

const formatTime = 'DD/MM/YYYY HH:mm';
export const FileSizeLimit = 262144000; // Limit size of upload file (262144000 = 250MB)
export const FileRowsLimit = 10000; // Limit rows of upload file (10000 rows)
export const uploadFileDefault: CsvFileInfosType = {
  nameFile: '',
  size: 0,
  totalNumberOfMembers: 0,
  membershipIDs: [],
  isMissingMembershipIDCol: false,
};

export type CreditVoucherPayload = {
  membershipType: {
    type: number;
    membershipIds: string[];
    membershipCSVs: string[];
    totalCsvRows: number | null;
  };
  ticketId: string;
  quantity: number;
  overrideQuantity: boolean;
  pushNotification: boolean;
  pushNotificationTitle: string;
  pushNotificationText: string;
  pushNotificationUrl: string;
  deductPoints: boolean;
  addToPocket: boolean;
  showExclusiveB2CBanner: boolean;
  showExclusiveB2CBannerText: string;
  showExclusiveB2CBannerCta: string;
  showExclusiveB2CBannerUrl: string;
  overrideVoucherType: boolean;
  syncToSiah: boolean;
  dryRun: boolean;
  visibility: boolean;
};

export const initialValuesDefault: CreditVoucherParams = {
  ticketId: null,
  membershipType: 1,
  membershipIds: [],
  membershipId: '',
  quantity: 0,
  deductPoints: 0,
  pushNotification: 1,
  pushNotificationText: '',
  pushNotificationTitle: '',
  pushNotificationUrl: '',
  addToPocket: 1, // default = 1 (true) if points required is FREE
  showExclusiveB2CBanner: 1, // default = false if points required is FREE
  showExclusiveB2CBannerText: '',
  showExclusiveB2CBannerCta: '',
  showExclusiveB2CBannerUrl: '',
  uploadFile: '',
  isValidForm: true,
};

export const initialTouchedDefault: any = {
  ticketId: false,
  membershipType: false,
  membershipIds: false,
  membershipId: false,
  quantity: false,
  deductPoints: false,
  pushNotification: false,
  pushNotificationText: false,
  pushNotificationTitle: false,
  pushNotificationUrl: false,
  addToPocket: false, // default = 1 (true) if points required is FREE
  showExclusiveB2CBanner: false, // default = false if points required is FREE
  showExclusiveB2CBannerText: false,
  showExclusiveB2CBannerCta: false,
  showExclusiveB2CBannerUrl: false,
  uploadFile: false,
  isValidForm: false,
};

export const targetedDistributionColumns = [
  {
    index: 'usageType',
    label: 'Usage Type',
  },
  {
    index: 'targetedDistribution',
    label: 'Visibility type',
  },
  {
    index: 'cost',
    label: 'Points Required',
  },
  {
    index: 'quantity',
    label: 'Total Voucher Count',
  },
  {
    index: 'redemptionCount',
    label: 'Get Voucher Count',
  },
  {
    index: 'validity',
    label: 'Validity',
  },
  {
    index: 'status',
    label: 'Status',
  },
];

export const quantityColumns = [
  {
    index: 'totalVouchersRequired',
    label: 'Total Vouchers Required',
  },
  {
    index: 'additionalVouchersRequired',
    label: 'Additional vouchers required',
  },
  {
    index: 'warning',
    label: '',
  },
];

export const MembershipTypeEnums = {
  SINGLE: 1,
  MULTIPLE: 2,
};

export const MembershipTypeOptions = [
  {
    key: 'Single specific member',
    value: MembershipTypeEnums.SINGLE,
  },
  {
    key: 'Multiple specific members',
    value: MembershipTypeEnums.MULTIPLE,
  },
];

export const AddToPocketOptions = [
  {
    key: 'Yes',
    value: 1,
  },
  {
    key: 'No (just notify)',
    value: 0,
  },
];

export const YesNoEnums = {
  YES: 1,
  NO: 0,
};

export const BooleanYesNoOptions = [
  {
    key: 'Yes',
    value: YesNoEnums.YES,
  },
  {
    key: 'No',
    value: YesNoEnums.NO,
  },
];

const generateUUID = () => {
  return ([1e7] + -1e3 + -4e3 + -8e3 + -1e11).replace(/[018]/g, c =>
    (
      c ^
      (crypto.getRandomValues(new Uint8Array(1))[0] & (15 >> (c / 4)))
    ).toString(16)
  );
};

export const handleChange = async <T,>(
  source: T,
  key: keyof T,
  value: T[keyof T] // Type-safe value parameter
): Promise<T> => {
  const res = {...source, [key]: value};
  return res;
};

export const getListTicketAvailable = async () => {
  const payload: object = {
    _size: 99999,
    _sort: 'id:desc',
    status_eq: 1,
    expireEndDateTime_gte: formatDateTime(new Date(), formatTime),
    expireStartDateTime_lte: formatDateTime(new Date(), formatTime),
  };
  const res = await new VoucherService().getAll(payload);
  return res;
};

export const getTicketDetail = async (id: string) => {
  const res = await new VoucherService().getOne(id);
  return res?.data?.data ?? null;
};

export const quantityRows = (
  ticketQuantity: number | null,
  ticketRedemptionCount: number | null,
  totalVouchersRequired: number | null,
  membershipsQuantity: number | 0,
  addToPocket: number
) => {
  if (
    IsNullOrEmptyOrUndefined(ticketQuantity) ||
    IsNullOrEmptyOrUndefined(ticketRedemptionCount) ||
    IsNullOrEmptyOrUndefined(totalVouchersRequired)
  ) {
    return {
      totalVouchersRequired: (
        <Typography variant="body2" fontSize={20} fontWeight="300">
          0
        </Typography>
      ),
      additionalVouchersRequired: (
        <Typography variant="body2" fontSize={20} fontWeight="300">
          0
        </Typography>
      ),
      warning: '',
    };
  }
  membershipsQuantity = membershipsQuantity <= 0 ? 1 : membershipsQuantity;
  ticketQuantity = Number(ticketQuantity);
  ticketRedemptionCount = Number(ticketRedemptionCount);
  totalVouchersRequired = Number(totalVouchersRequired) * membershipsQuantity;
  let ticketQuantityCanRedeem = 0;
  const isQuantityLimited = ticketQuantity !== -1;
  if (isQuantityLimited) {
    ticketQuantityCanRedeem = ticketQuantity - ticketRedemptionCount;
  }
  const isNotEnough =
    isQuantityLimited && totalVouchersRequired > ticketQuantityCanRedeem;
  let additionalVouchersRequiredValue: JSX.Element = (
    <Typography variant="body2" fontSize={20} fontWeight="300">
      0
    </Typography>
  );
  if (isNotEnough) {
    additionalVouchersRequiredValue = (
      <Typography
        variant="body2"
        fontSize={20}
        color="#DB252F"
        fontWeight="300"
      >
        {totalVouchersRequired - ticketQuantityCanRedeem}
      </Typography>
    );
  }
  const alert = (
    <>
      <img style={{verticalAlign: 'top'}} src={images.icon_triangle_alert} />
      <p
        style={{
          color: '#DB252F',
          fontWeight: 'bold',
          display: 'inline-block',
          margin: '0 12px',
        }}
      >
        {addToPocket === YesNoEnums.YES ? (
          <>
            This quantity will be added to the total voucher and get voucher{' '}
            <br />
            counts once the distribution is completed successfully.
          </>
        ) : (
          <>
            This quantity will be added to the total voucher count once the{' '}
            <br />
            distribution is completed successfully.
          </>
        )}
      </p>
    </>
  );

  return {
    totalVouchersRequired: (
      <Typography variant="body2" fontSize={20} fontWeight="300">
        {totalVouchersRequired}
      </Typography>
    ),
    additionalVouchersRequired: additionalVouchersRequiredValue,
    warning: isNotEnough ? alert : '',
  };
};

export const ContentElement = () => {
  return (
    <Grid item xs={12} mb={10} mt={-5}>
      <Table
        sx={{
          width: 'fit-content',
          margin: 'auto',
          '& .MuiTableCell-root': {
            border: '1px solid rgba(0, 0, 0, 0.2)',
            padding: '2px 4px',
            fontWeight: 'bolder',
          },
        }}
      >
        <TableHead>
          <TableRow>
            <TableCell>MembershipID</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          <TableRow>
            <TableCell>AA123456</TableCell>
          </TableRow>
          <TableRow>
            <TableCell>AA098765</TableCell>
          </TableRow>
          <TableRow>
            <TableCell>AA999999</TableCell>
          </TableRow>
        </TableBody>
      </Table>
    </Grid>
  );
};

export const renderTargetedDistributionsColumns = (
  index: string,
  dataDetails: any
) => {
  if (dataDetails === null) {
    return '--';
  }

  if (index === 'status') {
    return renderTicketStatus(dataDetails);
  } else if (index === 'usageType') {
    return renderTicketUsageType(dataDetails.usageType);
  } else if (index === 'targetedDistribution') {
    return renderTicketVisibility(dataDetails);
  } else if (index === 'cost') {
    return dataDetails?.[index] > 0 ? dataDetails?.[index] : 'Free';
  } else if (index === 'quantity') {
    return dataDetails?.[index] < 0 ? 'Unlimited' : dataDetails?.[index];
  } else if (index === 'validity') {
    return renderTicketValidity(dataDetails, formatTime);
  }
  if (dataDetails[index] === 0) {
    return 0;
  }
  // Render default value
  return dataDetails[index] ?? '--';
};

export const isDisplayUsageTypeAlert = (
  usageType: number | null,
  pointsRequired: number | null
) => {
  if (usageType === null || pointsRequired === null) {
    return false;
  }
  if (
    usageType ===
      ticketUsageTypeRadioOptions[TicketUsageTypeEnums.MULTI_REDEMPTION]
        .value &&
    pointsRequired > 0
  )
    return false;
  return true;
};

export const renderUsageTypeAlert = (
  usageType: number,
  pointsRequired: number
) => {
  if (usageType === null) {
    return '';
  }
  switch (Number(usageType)) {
    case TicketUsageTypeEnums.SINGLE_NON_REPEATABLE:
      return 'Voucher will not be distributed to members who have previously redeemed or used this voucher.';
    case TicketUsageTypeEnums.MULTI_REDEMPTION:
      if (pointsRequired !== null && pointsRequired > 0) return '';
      return 'Members who have voucher in their pocket won’t receive nor be able to redeem.';
    case TicketUsageTypeEnums.SINGLE_REPEATABLE:
      return 'Members who have voucher in their pocket won’t receive nor be able to redeem.';
    default:
      return '';
  }
};

// Func render component
export const TargetedDistributionsTable = ({
  columns,
  rows = null,
  ...props
}: {
  columns: any;
  rows: any;
  isFullWidth?: boolean;
}) => {
  if (rows === null) return <></>;
  const {isFullWidth = true} = props;
  return (
    <TableContainer component={Paper}>
      <Table
        aria-label="usage table"
        sx={{width: isFullWidth ? '100%' : 'fit-content'}}
      >
        <TableHead>
          <TableRow>
            {columns.map((row, index) => (
              <TableCell
                sx={{
                  borderBottom: 'unset',
                  fontSize: '11px',
                  padding: '4px 16px 4px 0px',
                  minWidth: columns.length < 4 ? '205px' : 'auto',
                }}
                key={index}
              >
                {row.label}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          <TableRow>
            {columns.map((row, index) => (
              <TableCell
                sx={{
                  borderBottom: 'unset',
                  paddingTop: 0,
                  fontSize: '11px',
                  padding: '4px 16px 4px 0px',
                }}
                key={index}
              >
                {renderTargetedDistributionsColumns(row.index, rows) ?? '--'}
              </TableCell>
            ))}
          </TableRow>
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export const formatBytes = (bytes, decimals = 2) => {
  if (!+bytes) return '0 Bytes';

  const k = 1024;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];

  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(dm))} ${sizes[i]}`;
};

// export const UploadChunk = async (chunk, index, totalChunks) => {
//   const formData = new FormData();
//   const uniqueFileID = 'file-12345'; // This should be a unique ID for the file
//   formData.append('file', chunk); // The current chunk of the file
//   formData.append('chunkIndex', index); // The position of this chunk
//   formData.append('totalChunks', totalChunks); // Total chunks count for reference
//   formData.append('fileID', uniqueFileID); // Unique ID for identifying the file

//   try {
//     const response = await fetch('/upload-endpoint', {
//       method: 'POST',
//       body: formData,
//     });

//     if (!response.ok) {
//       throw new Error(`Chunk ${index + 1} of ${totalChunks} failed to upload`);
//     }

//     console.log(`Chunk ${index + 1} of ${totalChunks} uploaded successfully`);
//   } catch (error) {
//     console.error('Error uploading chunk:', error);
//     // Optional: retry logic can be added here for robustness
//   }
// };

export const createCSVFile = async (
  guuid: string,
  index: number,
  csvContentProp: string | string[]
) => {
  // Define the column header and data rows
  // const header = 'MembershipID';
  // const rows = membershipIds;

  // // Combine header and rows into a CSV string
  // const csvContent = [header, ...rows].join('\n');

  // Create a Blob with the CSV content
  const csvContent = Array.isArray(csvContentProp)
    ? csvContentProp.join('\n')
    : csvContentProp;
  const blob = new Blob([csvContent], {type: 'text/csv;charset=utf-8;'});
  // Create a File object
  const csvFile = new File([blob], `${guuid}_${index}.csv`, {
    type: 'text/csv',
  });

  return csvFile;
};

export const onChooseCSVFile = async (
  event: React.ChangeEvent<HTMLInputElement>,
  setCsvFiles: (e: any) => {}
): Promise<CsvFileInfosType> => {
  const csvFiles: File[] = [];
  const csvFileGuuid = generateUUID();
  let csvFilePart = 0;
  let csvFileCollection: CsvFileInfosType = uploadFileDefault;
  const file = event.target.files ? event.target.files[0] : null;
  const CHUNK_SIZE = 20 * 1024 * 1024; // 20MB chunk size

  if (!file) {
    return csvFileCollection;
  }
  // Initialize file metadata
  csvFileCollection = {
    ...csvFileCollection,
    nameFile: file.name,
    size: file.size,
  };

  const totalChunks = Math.ceil(file.size / CHUNK_SIZE);
  const membershipIDs: string[] = [];
  let indexOfMembershipIDInArray: number | null = null;
  const processChunk = async (chunk: string, isFirstChunk: boolean) => {
    const membershipIdsPerCSV: string[] = [];
    const rows = chunk.split('\n');
    rows.forEach((row, index) => {
      const columns = row.split(row.includes(',') ? ',' : ';');
      const isRowBody = !(isFirstChunk && index === 0); // Skip header if first chunk
      // Identify 'MembershipID' column index in the header row
      const rowString = row.replace(/[\r\n\s]+/g, '');
      if (isFirstChunk && index === 0 && rowString.includes('MembershipID')) {
        columns.forEach((item, indexItem) => {
          const columnsItemString = item.replace(/[\r\n\s]+/g, '');
          if (columnsItemString === 'MembershipID')
            indexOfMembershipIDInArray = indexItem;
        });
      } else if (
        isFirstChunk &&
        index === 0 &&
        !rowString.includes('MembershipID')
      ) {
        csvFileCollection.isMissingMembershipIDCol = true;
      }

      // If MembershipID column index found, collect IDs from the body rows
      if (
        indexOfMembershipIDInArray !== null && // Validate Csv file contained the MembershipID column
        isRowBody && // Validate body row
        row.replace(/\s/g, '').length > 0 // Validate empty rows
      ) {
        membershipIDs.push(columns[indexOfMembershipIDInArray]);
        membershipIdsPerCSV.push(columns[indexOfMembershipIDInArray]);
      }
    });

    const data = rows;
    const chunkRows = FileRowsLimit;
    let currentRows = 0;
    if (data.length > chunkRows) {
      const loop = Math.ceil(data.length / chunkRows);
      for (let index = 1; index <= loop; index++) {
        csvFilePart++;
        csvFiles.push(
          await createCSVFile(
            csvFileGuuid,
            csvFilePart,
            data.slice(currentRows, index * chunkRows)
          )
        );
        currentRows = index * chunkRows;
      }
    } else {
      csvFilePart++;
      csvFiles.push(await createCSVFile(csvFileGuuid, csvFilePart, data));
    }
  };

  return new Promise((resolve, reject) => {
    let currentChunk = 0;

    const readNextChunk = () => {
      const start = currentChunk * CHUNK_SIZE;
      const end = Math.min(start + CHUNK_SIZE, file.size);
      const blob = file.slice(start, end);
      const reader = new FileReader();

      reader.onload = async () => {
        const chunkText = reader.result as string;
        await processChunk(chunkText, currentChunk === 0);

        currentChunk++;
        if (currentChunk < totalChunks) {
          readNextChunk();
        } else {
          // Finalize file information and resolve promise after reading all chunks
          csvFileCollection = {
            ...csvFileCollection,
            totalNumberOfMembers: membershipIDs.length,
            membershipIDs,
          };
          setCsvFiles(csvFiles);
          resolve(csvFileCollection);
        }
      };

      reader.onerror = () => {
        reject(new Error('Failed to read file chunk'));
      };

      reader.readAsText(blob);
    };

    // Start reading the first chunk
    readNextChunk();
  });
};

export const VerifyMembershipID = async (membershipId: string) => {
  try {
    const res = await new MembersService().get(
      `verify-membership-id/${membershipId}`
    );
    return res?.data?.data ? true : false;
  } catch (err) {
    return false;
  }
};
